import { ARENA } from './balance'
import type { AdvDissolve } from './adv'

// 会話の終わりに、 ボスが光になって消える層 (2026-10-29 作者「会話の最後に消えていって、光になって」)。
//   ボスの体が白く光り、 光の粒がほどけていく。 そのあいだに外から闇が寄ってきて、 最後に光ったボスも消える。 消えきると画面は黒。
//   (ボスから闇を広げる形は「黒い染み」に見えたため、 10-29 にこの順へ変えた。)
//   案 A＝銀の粒がその場で薄れる・案 B＝青白い粒が上へ流れる (rise)。 絵は 16:9 で画面いっぱいなので、 中心は絵に対する割合のまま使う。
const W = 160, H = 90
let noise: Float32Array | null = null
function noiseField(): Float32Array {
  if (noise) return noise
  // 決まった種の値ノイズを3段重ね (毎回同じ縁になる)。
  let seed = 7
  const rnd = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647 }
  const out = new Float32Array(W * H)
  for (const [cell, weight] of [[16, .55], [8, .3], [4, .15]] as const) {
    const gw = Math.ceil(W / cell) + 2, gh = Math.ceil(H / cell) + 2
    const grid = Array.from({ length: gw * gh }, rnd)
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const fx = x / cell, fy = y / cell, ix = Math.floor(fx), iy = Math.floor(fy)
      const sx = (fx - ix) ** 2 * (3 - 2 * (fx - ix)), sy = (fy - iy) ** 2 * (3 - 2 * (fy - iy))
      const g = (i: number, j: number) => grid[j * gw + i]!
      const top = g(ix, iy) + (g(ix + 1, iy) - g(ix, iy)) * sx, bottom = g(ix, iy + 1) + (g(ix + 1, iy + 1) - g(ix, iy + 1)) * sx
      out[y * W + x]! += (top + (bottom - top) * sy) * weight
    }
  }
  return (noise = out)
}
// 消える順番 o (0=最初・1=最後。 外から暗くなり、 ボスは最後まで光って残る) と、 ボスからの距離 d。 中心ごとに1度だけ作る。
const orders = new Map<string, { o: Float32Array; d: Float32Array }>()
function orderField(v: AdvDissolve) {
  const key = `${v.x},${v.y},${v.rx},${v.ry}`
  let f = orders.get(key)
  if (f) return f
  const n = noiseField(), o = new Float32Array(W * H), d = new Float32Array(W * H)
  let lo = Infinity, hi = -Infinity
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x
    const dist = Math.hypot((x / W - v.x) / v.rx, (y / H - v.y) / v.ry)
    d[i] = dist
    o[i] = .78 * (1 - Math.min(1, dist / 2.5)) + .22 * n[i]!
    lo = Math.min(lo, o[i]!); hi = Math.max(hi, o[i]!)
  }
  for (let i = 0; i < o.length; i++) o[i] = (o[i]! - lo) / (hi - lo || 1)
  f = { o, d }; orders.set(key, f)
  return f
}
let layer: { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; data: ImageData } | null = null
function layerCanvas() {
  if (layer) return layer
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H
  const ctx = canvas.getContext('2d'); if (!ctx) return null
  return (layer = { canvas, ctx, data: ctx.createImageData(W, H) })
}
const hash = (i: number, k: number) => { const s = Math.sin(i * 127.1 + k * 311.7) * 43758.5453; return s - Math.floor(s) }
const EDGE = .16, GLOW = .2

export function drawAdvDissolve(ctx: CanvasRenderingContext2D, v: AdvDissolve): void {
  if (v.p >= 1) { ctx.fillStyle = '#000'; ctx.fillRect(0, 0, ARENA.width, ARENA.height); return }
  if (v.p <= 0) return
  const l = layerCanvas(); if (!l) return
  const { o, d } = orderField(v), n = noiseField(), T = v.p * 1.15 - .05
  const [r, g, b] = v.tint === 'blue' ? [168, 204, 255] : [226, 230, 240]
  const px = l.data.data
  // 1枚目＝闇。 縁の光は2枚目で足す (同じ ImageData を使い回す)。
  for (let i = 0; i < W * H; i++) {
    const a = Math.min(1, Math.max(0, (T - o[i]!) / EDGE))
    px[i * 4] = 0; px[i * 4 + 1] = 0; px[i * 4 + 2] = 0; px[i * 4 + 3] = a * 255
  }
  l.ctx.putImageData(l.data, 0, 0)
  ctx.save()
  ctx.imageSmoothingEnabled = true
  ctx.drawImage(l.canvas, 0, 0, ARENA.width, ARENA.height)
  // 縁の光はボスの近くだけ (遠くの壁や床は光らせず、 静かに暗くなる)。 終わりに向けて弱める。
  const fadeOut = Math.min(1, (1 - v.p) / .25)
  for (let i = 0; i < W * H; i++) {
    const near = Math.max(0, 1 - d[i]! / 1.4)
    // 闇の前の帯が明るく光り、 まだ残っている体もむらのある光で満ちる (体が光になってから、 ほどける)。
    const ahead = o[i]! - T
    const front = ahead >= 0 ? Math.max(0, 1 - ahead / GLOW) : Math.max(0, 1 + ahead / EDGE)
    const body = ahead > 0 ? Math.max(0, 1 - d[i]! / 1.05) * (.5 + .9 * n[i]!) * Math.min(1, v.p / .4) * .75 : 0
    const a = Math.min(1, front * near + body) * fadeOut
    px[i * 4] = r; px[i * 4 + 1] = g; px[i * 4 + 2] = b; px[i * 4 + 3] = a * 210
  }
  l.ctx.putImageData(l.data, 0, 0)
  ctx.globalCompositeOperation = 'lighter'
  ctx.drawImage(l.canvas, 0, 0, ARENA.width, ARENA.height)
  // 光の粒: 光ったボスの体からほどけて薄れる。 状態を持たず p と番号だけで決める。
  const count = 150, life = .34
  for (let k = 0; k < count; k++) {
    const ang = hash(k, 1) * Math.PI * 2, rad = Math.sqrt(hash(k, 2)) * .95
    const fx = v.x + Math.cos(ang) * rad * v.rx, fy = v.y + Math.sin(ang) * rad * v.ry
    const born = .12 + hash(k, 3) * .62, age = (v.p - born) / life
    if (age <= 0 || age >= 1) continue
    const sway = Math.sin(v.el * .002 + k) * 6
    const lift = v.rise ? age * (40 + hash(k, 4) * 90) : age * (4 + hash(k, 4) * 8)
    const sx = fx * ARENA.width + sway * (v.rise ? 1 : .3), sy = fy * ARENA.height - lift
    const size = (v.rise ? 2 : 1.6) + hash(k, 5) * 2
    const alpha = Math.sin(Math.PI * age) * (v.rise ? .9 : .75) * fadeOut
    const glow = ctx.createRadialGradient(sx, sy, 0, sx, sy, size * 4)
    glow.addColorStop(0, `rgba(${r},${g},${b},${alpha})`); glow.addColorStop(1, `rgba(${r},${g},${b},0)`)
    ctx.fillStyle = glow; ctx.fillRect(sx - size * 4, sy - size * 4, size * 8, size * 8)
  }
  ctx.restore()
}
